/**
 * UploadStatusChip - the one visual vocabulary for "where is this photo on
 * its way to the server?"
 *
 * Background uploading only works as a UX if the user can see it happening.
 * This atom is the shared read-out: a small, dense chip that sits over a
 * thumbnail or beside a checklist row and says uploading / uploaded /
 * waiting for signal / failed. Every capture surface uses this same chip so
 * the states mean the same thing everywhere in the app.
 *
 * The failed state is interactive when `onRetry` is supplied — the chip
 * itself becomes the retry affordance, which keeps the recovery action
 * exactly where the user is already looking instead of in a separate banner.
 *
 * @example
 * <UploadStatusChip status="uploading" />
 * <UploadStatusChip status="failed" error="Network error" onRetry={handleRetry} />
 */
import React from 'react';
import type { PhotoUploadStatus } from '../../../hooks/usePhotoUploadQueue';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Chip scale. 'sm' fits a 72px thumbnail; 'md' suits a full-width row. */
export type UploadStatusChipSize = 'sm' | 'md';

export interface UploadStatusChipProps {
    /** Which lifecycle state to render. */
    status: PhotoUploadStatus;
    /** Failure detail, surfaced as the chip's title tooltip. */
    error?: string | null;
    /** When provided, the failed chip becomes a retry button. */
    onRetry?: () => void;
    /** Size token. Defaults to 'sm'. */
    size?: UploadStatusChipSize;
    /** Optional className appended to the root element. */
    className?: string;
}

/* ============================================
 * SIZE TOKENS
 * ============================================ */

const SIZE_CLASSES: Record<UploadStatusChipSize, string> = {
    sm: 'text-10px gap-4px px-6px py-2px',
    md: 'text-12px gap-6px px-8px py-4px',
};

const ICON_CLASSES: Record<UploadStatusChipSize, string> = {
    sm: 'w-10px h-10px',
    md: 'w-14px h-14px',
};

/* ============================================
 * ICONS
 * ============================================ */

/** Spinner shown while bytes are moving. */
const SpinnerIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={`animate-spin ${className ?? ''}`} fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
);

/** Checkmark for the settled, on-the-server state. */
const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
);

/** Struck-through cloud — "held on this device, not sent yet". */
const CloudOffIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 2.06-3.03m3.2-1.24a5.25 5.25 0 0 1 5.01 3.9 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
    </svg>
);

/** Retry arrows, shown alongside the failure text when retry is wired. */
const RetryIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
    </svg>
);

/* ============================================
 * COMPONENT
 * ============================================ */

export const UploadStatusChip: React.FC<UploadStatusChipProps> = ({ status, error, onRetry, size = 'sm', className }) => {
    const base = ['upload-status-chip', `upload-status-chip--${status}`, 'inline-flex items-center font-sans font-semibold rounded-full backdrop-blur-sm whitespace-nowrap', SIZE_CLASSES[size]];
    const iconClass = ICON_CLASSES[size];

    /* Failed is the only state that can be interactive, so it renders as a
       <button> when a retry handler exists and a <span> otherwise. */
    if (status === 'failed') {
        const label = onRetry ? 'Tap to retry' : 'Upload failed';
        const content = (
            <>
                <RetryIcon className={iconClass} />
                <span>{label}</span>
            </>
        );

        /* The chip is an overlay on a photo thumbnail, so it can't grow to
           44px without covering the picture it belongs to. The visible chip
           stays 32px and a transparent ::before extends the target past the
           floor — CONVENTIONS.md §5, option 2. Safe here because nothing sits
           beside it to mis-tap. */
        return onRetry ? (
            <button type="button" onClick={(e) => { e.stopPropagation(); onRetry(); }} className={[...base, 'min-h-32px relative before:absolute before:-inset-8px before:content-[\'\'] bg-red-600/90 text-white hover:bg-red-700', className ?? ''].join(' ')} data-ref="atom-upload-status-chip" title={error ?? 'Upload failed — tap to retry'} aria-label={error ? `Upload failed: ${error}. Tap to retry.` : 'Upload failed. Tap to retry.'}>
                {content}
            </button>
        ) : (
            <span className={[...base, 'bg-red-600/90 text-white', className ?? ''].join(' ')} data-ref="atom-upload-status-chip" title={error ?? 'Upload failed'}>
                {content}
            </span>
        );
    }

    if (status === 'uploaded') {
        return (
            <span className={[...base, 'bg-green-600/90 text-white', className ?? ''].join(' ')} data-ref="atom-upload-status-chip" title="Uploaded">
                <CheckIcon className={iconClass} />
                <span>Uploaded</span>
            </span>
        );
    }

    if (status === 'waiting') {
        return (
            <span className={[...base, 'bg-amber-500/90 text-white', className ?? ''].join(' ')} data-ref="atom-upload-status-chip" title="Saved on this device — it will upload when you're back on a signal">
                <CloudOffIcon className={iconClass} />
                <span>Waiting for signal</span>
            </span>
        );
    }

    /* status === 'uploading' */
    return (
        <span className={[...base, 'bg-black/70 text-white', className ?? ''].join(' ')} data-ref="atom-upload-status-chip" title="Uploading in the background">
            <SpinnerIcon className={iconClass} />
            <span>Uploading…</span>
        </span>
    );
};

export default UploadStatusChip;
